/**
 * Tiny procedural RPG SFX kit — no wave assets.
 * Call unlockAudio() once after a user gesture (pointer/key).
 * Mute via setMuted(); all cues no-op when muted or AudioContext suspended.
 */

export type BeepOpts = {
  freq: number;
  dur?: number;
  type?: OscillatorType;
  gain?: number;
  /** Absolute Hz delta applied over dur (exponential ramp). */
  slide?: number;
};

export type NoiseOpts = {
  dur?: number;
  gain?: number;
  /** High-pass cutoff (Hz). */
  hp?: number;
  /** Low-pass cutoff (Hz). */
  lp?: number;
};

let ctx: AudioContext | null = null;
let muted = false;
let masterGain = 1;

function ac(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
}

export function unlockAudio(): void {
  const c = ac();
  if (!c) return;
  if (c.state === "suspended") void c.resume();
}

export function setMuted(next: boolean): void {
  muted = next;
}

export function isMuted(): boolean {
  return muted;
}

/** 0..1 linear scale applied on top of per-cue gains. */
export function setMasterGain(g: number): void {
  masterGain = Math.max(0, Math.min(1, g));
}

export function getMasterGain(): number {
  return masterGain;
}

function live(): AudioContext | null {
  if (muted || masterGain <= 0) return null;
  const c = ac();
  if (!c || c.state === "suspended") return null;
  return c;
}

function g(v: number): number {
  return v * masterGain;
}

export function beep(opts: BeepOpts): void {
  const c = live();
  if (!c) return;
  const dur = opts.dur ?? 0.08;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = opts.type ?? "square";
  osc.frequency.setValueAtTime(opts.freq, c.currentTime);
  if (opts.slide) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(40, opts.freq + opts.slide),
      c.currentTime + dur,
    );
  }
  const amp = g(opts.gain ?? 0.04);
  gain.gain.setValueAtTime(amp, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + dur + 0.02);
}

/** Filtered white-noise burst — thuds, whooshes, cracks. */
export function noiseBurst(opts: NoiseOpts = {}): void {
  const c = live();
  if (!c) return;
  const dur = opts.dur ?? 0.08;
  const frames = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, frames, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;

  const src = c.createBufferSource();
  src.buffer = buf;
  let node: AudioNode = src;

  if (opts.hp && opts.hp > 0) {
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = opts.hp;
    node.connect(hp);
    node = hp;
  }
  if (opts.lp && opts.lp > 0) {
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = opts.lp;
    node.connect(lp);
    node = lp;
  }

  const gain = c.createGain();
  const amp = g(opts.gain ?? 0.04);
  gain.gain.setValueAtTime(amp, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  node.connect(gain);
  gain.connect(c.destination);
  src.start();
  src.stop(c.currentTime + dur + 0.02);
}

function later(ms: number, fn: () => void): void {
  if (typeof window === "undefined") return;
  window.setTimeout(fn, ms);
}

/** RPG cue map — distinct pitch / length / timbre per combat role. */
export const SfxCue = {
  // —— legacy FFA / shared ——
  hit: () => {
    noiseBurst({ dur: 0.045, gain: 0.035, lp: 900 });
    beep({ freq: 180, dur: 0.06, type: "square", gain: 0.028, slide: -60 });
  },
  death: () => {
    beep({ freq: 160, dur: 0.22, type: "sawtooth", gain: 0.04, slide: -100 });
    later(40, () => noiseBurst({ dur: 0.12, gain: 0.03, lp: 600 }));
  },
  masterDeath: () => {
    noiseBurst({ dur: 0.2, gain: 0.05, lp: 400 });
    beep({ freq: 90, dur: 0.4, type: "sawtooth", gain: 0.055, slide: -45 });
    later(90, () =>
      beep({ freq: 70, dur: 0.28, type: "square", gain: 0.03, slide: -30 }),
    );
  },
  summon: () => {
    beep({ freq: 330, dur: 0.08, type: "triangle", gain: 0.03, slide: 120 });
    later(50, () =>
      beep({ freq: 520, dur: 0.1, type: "triangle", gain: 0.035, slide: 180 }),
    );
  },
  collect: () => beep({ freq: 660, dur: 0.07, type: "sine", gain: 0.035 }),
  jackpot: () => {
    beep({ freq: 520, dur: 0.12, type: "triangle", gain: 0.05 });
    later(80, () => beep({ freq: 780, dur: 0.14, type: "triangle", gain: 0.05 }));
  },
  goal: () => beep({ freq: 380, dur: 0.05, type: "sine", gain: 0.025 }),

  // —— raid / RPG kit ——
  /** Whoosh on attack windup / origin. */
  swing: () => {
    noiseBurst({ dur: 0.07, gain: 0.028, hp: 800, lp: 4200 });
    beep({ freq: 420, dur: 0.045, type: "triangle", gain: 0.018, slide: -180 });
  },
  /** Melee flesh/armor thud (tank AA, add AA, generic). */
  meleeHit: () => {
    noiseBurst({ dur: 0.055, gain: 0.045, lp: 700 });
    beep({ freq: 140, dur: 0.07, type: "square", gain: 0.032, slide: -50 });
    later(18, () =>
      beep({ freq: 90, dur: 0.04, type: "sawtooth", gain: 0.02 }),
    );
  },
  /** Arrow / staff bolt impact — brighter ping. */
  boltHit: () => {
    beep({ freq: 880, dur: 0.04, type: "sine", gain: 0.03 });
    beep({ freq: 440, dur: 0.07, type: "triangle", gain: 0.025, slide: -200 });
    noiseBurst({ dur: 0.035, gain: 0.022, hp: 1200, lp: 5000 });
  },
  /** Marked shot charge / release — sharp amber sting. */
  markedShot: () => {
    beep({ freq: 620, dur: 0.06, type: "sawtooth", gain: 0.035, slide: 220 });
    later(35, () => {
      noiseBurst({ dur: 0.05, gain: 0.03, hp: 600, lp: 3500 });
      beep({ freq: 280, dur: 0.08, type: "square", gain: 0.028, slide: -80 });
    });
  },
  /** Marked shot impact — heavier than bolt. */
  markedShotHit: () => {
    beep({ freq: 520, dur: 0.05, type: "sawtooth", gain: 0.04 });
    noiseBurst({ dur: 0.08, gain: 0.04, lp: 1400 });
    later(25, () =>
      beep({ freq: 200, dur: 0.1, type: "square", gain: 0.03, slide: -70 }),
    );
  },
  /** Heal cast chime (soft rising). */
  healCast: () => {
    beep({ freq: 523, dur: 0.06, type: "sine", gain: 0.022 });
    later(40, () => beep({ freq: 659, dur: 0.07, type: "sine", gain: 0.02 }));
  },
  /** Heal land — major triad sparkle. */
  heal: () => {
    beep({ freq: 523, dur: 0.09, type: "sine", gain: 0.028 });
    later(45, () => beep({ freq: 659, dur: 0.1, type: "sine", gain: 0.026 }));
    later(90, () => beep({ freq: 784, dur: 0.14, type: "triangle", gain: 0.024 }));
  },
  /** Shield bash clang. */
  shieldBash: () => {
    noiseBurst({ dur: 0.04, gain: 0.04, hp: 400, lp: 2800 });
    beep({ freq: 360, dur: 0.05, type: "triangle", gain: 0.04 });
    later(20, () =>
      beep({ freq: 240, dur: 0.09, type: "square", gain: 0.03, slide: -90 }),
    );
  },
  /** Boss slam impact — low boom. */
  bossSlam: () => {
    noiseBurst({ dur: 0.16, gain: 0.055, lp: 350 });
    beep({ freq: 70, dur: 0.2, type: "sawtooth", gain: 0.05, slide: -25 });
    later(40, () =>
      beep({ freq: 110, dur: 0.1, type: "square", gain: 0.025 }),
    );
  },
  /** Boss brass cone — magic dissonance. */
  bossBrass: () => {
    beep({ freq: 310, dur: 0.14, type: "sawtooth", gain: 0.035, slide: 80 });
    beep({ freq: 466, dur: 0.12, type: "sawtooth", gain: 0.028, slide: -40 });
    noiseBurst({ dur: 0.1, gain: 0.03, hp: 900, lp: 4000 });
  },
  /** Leap telegraph whoosh. */
  bossLeapOpen: () => {
    noiseBurst({ dur: 0.12, gain: 0.035, hp: 500, lp: 3000 });
    beep({ freq: 180, dur: 0.12, type: "triangle", gain: 0.025, slide: 220 });
  },
  /** Leap land crater. */
  bossLeap: () => {
    noiseBurst({ dur: 0.2, gain: 0.06, lp: 280 });
    beep({ freq: 55, dur: 0.28, type: "sawtooth", gain: 0.055, slide: -20 });
    later(50, () => noiseBurst({ dur: 0.1, gain: 0.035, lp: 800 }));
  },
  /** Toughness break / stun shatter. */
  breakStun: () => {
    noiseBurst({ dur: 0.1, gain: 0.05, hp: 1500, lp: 8000 });
    beep({ freq: 880, dur: 0.06, type: "square", gain: 0.035, slide: -400 });
    later(50, () =>
      beep({ freq: 220, dur: 0.18, type: "sawtooth", gain: 0.04, slide: -100 }),
    );
  },
  /** Ally / add death. */
  raidDeath: () => {
    beep({ freq: 200, dur: 0.2, type: "sawtooth", gain: 0.038, slide: -120 });
    later(60, () => noiseBurst({ dur: 0.14, gain: 0.028, lp: 500 }));
  },
  /** Boss death. */
  raidBossDeath: () => {
    noiseBurst({ dur: 0.25, gain: 0.055, lp: 350 });
    beep({ freq: 80, dur: 0.45, type: "sawtooth", gain: 0.055, slide: -40 });
    later(120, () =>
      beep({ freq: 55, dur: 0.35, type: "square", gain: 0.03, slide: -15 }),
    );
  },
  /** Thrall / add spawn. */
  spawnAdd: () => {
    noiseBurst({ dur: 0.06, gain: 0.025, hp: 200, lp: 2000 });
    beep({ freq: 180, dur: 0.08, type: "triangle", gain: 0.03, slide: 160 });
    later(55, () =>
      beep({ freq: 360, dur: 0.1, type: "triangle", gain: 0.028, slide: 200 }),
    );
  },
  /** Charger (amber spider) appear — brighter / higher than spawnAdd. */
  spawnCharger: () => {
    noiseBurst({ dur: 0.07, gain: 0.03, hp: 250, lp: 2800 });
    beep({ freq: 240, dur: 0.09, type: "triangle", gain: 0.034, slide: 220 });
    later(50, () =>
      beep({ freq: 480, dur: 0.11, type: "sine", gain: 0.03, slide: 160 }),
    );
  },
  /** Quiet commander order commit. */
  uiCommit: () => {
    beep({ freq: 480, dur: 0.035, type: "sine", gain: 0.016 });
    later(30, () => beep({ freq: 640, dur: 0.04, type: "sine", gain: 0.012 }));
  },
  /** Party taking damage (softer than outgoing hit). */
  hitTaken: () => {
    noiseBurst({ dur: 0.05, gain: 0.03, lp: 1100 });
    beep({ freq: 260, dur: 0.05, type: "square", gain: 0.022, slide: -90 });
  },
  /** Knockback body thud. */
  knock: () => {
    noiseBurst({ dur: 0.06, gain: 0.035, lp: 650 });
    beep({ freq: 120, dur: 0.07, type: "square", gain: 0.025, slide: -40 });
  },
  /** Heavy knock (AoE). */
  knockHeavy: () => {
    noiseBurst({ dur: 0.1, gain: 0.045, lp: 500 });
    beep({ freq: 95, dur: 0.12, type: "sawtooth", gain: 0.035, slide: -35 });
  },
  /** Soft land after knock flight. */
  knockLand: () => {
    noiseBurst({ dur: 0.07, gain: 0.028, lp: 900 });
  },
  /** Boss AA origin — heavier whoosh. */
  bossSwing: () => {
    noiseBurst({ dur: 0.1, gain: 0.04, hp: 200, lp: 1800 });
    beep({ freq: 90, dur: 0.08, type: "sawtooth", gain: 0.03, slide: -30 });
  },
  /** Boss AA hit on party. */
  bossMelee: () => {
    noiseBurst({ dur: 0.08, gain: 0.05, lp: 550 });
    beep({ freq: 100, dur: 0.1, type: "square", gain: 0.038, slide: -40 });
  },
  /** Telegraph open (soft). */
  teleOpen: () => {
    beep({ freq: 240, dur: 0.08, type: "triangle", gain: 0.018, slide: 60 });
    noiseBurst({ dur: 0.06, gain: 0.015, hp: 400, lp: 2500 });
  },
} as const;

export type SfxCueName = keyof typeof SfxCue;
